import { cn } from "@/lib/utils";
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { type VariantProps, cva } from "class-variance-authority";
import { CloseButton } from "@/components/ui/BackButton";
import SheetOverlay from "./SheetOverlay";

const sheetContentVariants = cva(
  // Enter slides in over 300ms on the signature ease-out; exit is quicker
  // (200ms). motion-reduce drops the slide (no global reduced-motion reset).
  "fixed z-50 flex flex-col gap-4 bg-card p-6 shadow-lg focus-visible:outline-none data-[state=closed]:duration-200 data-[state=open]:duration-300 data-[state=open]:ease-signature data-[state=open]:animate-in data-[state=closed]:animate-out motion-reduce:!animate-none",
  {
    variants: {
      side: {
        bottom:
          "inset-x-0 bottom-0 rounded-t-2xl border-t border-border data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        top: "inset-x-0 top-0 rounded-b-2xl border-b border-border data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        right:
          "inset-y-0 right-0 rounded-l-2xl border-l border-border data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
        left: "inset-y-0 left-0 rounded-r-2xl border-r border-border data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
      },
      size: {
        sm: "",
        md: "",
        lg: "",
        xl: "",
        full: "",
      },
    },
    compoundVariants: [
      // side=bottom / side=top → size controls height; full viewport width
      { side: "bottom", size: "sm", class: "h-[40vh]" },
      { side: "bottom", size: "md", class: "h-[60vh]" },
      { side: "bottom", size: "lg", class: "h-[80vh]" },
      { side: "bottom", size: "xl", class: "h-[90vh]" },
      { side: "bottom", size: "full", class: "h-screen rounded-none" },
      { side: "top", size: "sm", class: "h-[40vh]" },
      { side: "top", size: "md", class: "h-[60vh]" },
      { side: "top", size: "lg", class: "h-[80vh]" },
      { side: "top", size: "xl", class: "h-[90vh]" },
      { side: "top", size: "full", class: "h-screen rounded-none" },
      // side=right / side=left → size controls width; full viewport height
      { side: "right", size: "sm", class: "h-full w-[320px] max-w-[85vw]" },
      { side: "right", size: "md", class: "h-full w-[400px] max-w-[85vw]" },
      { side: "right", size: "lg", class: "h-full w-[600px] max-w-[90vw]" },
      { side: "right", size: "xl", class: "h-full w-[800px] max-w-[90vw]" },
      { side: "right", size: "full", class: "h-full w-screen rounded-none" },
      { side: "left", size: "sm", class: "h-full w-[320px] max-w-[85vw]" },
      { side: "left", size: "md", class: "h-full w-[400px] max-w-[85vw]" },
      { side: "left", size: "lg", class: "h-full w-[600px] max-w-[90vw]" },
      { side: "left", size: "xl", class: "h-full w-[800px] max-w-[90vw]" },
      { side: "left", size: "full", class: "h-full w-screen rounded-none" },
    ],
    defaultVariants: {
      side: "right",
      size: "md",
    },
  }
);

type SheetContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> &
  VariantProps<typeof sheetContentVariants> & {
    hideCloseButton?: boolean;
    // Applied to the sibling overlay. Required when callers pass a responsive
    // visibility class (e.g. `md:hidden`) to `className` — without the same
    // class on the overlay, the dark backdrop stays visible at breakpoints
    // where the content is hidden.
    overlayClassName?: string;
  };

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      className,
      children,
      hideCloseButton,
      side,
      size,
      onPointerDownOutside,
      overlayClassName,
      ...props
    },
    ref
  ) => {
    return (
      <DialogPrimitive.Portal>
        <SheetOverlay className={overlayClassName} />
        <DialogPrimitive.Content
          ref={ref}
          onPointerDownOutside={(e) => {
            if (e.detail.originalEvent.pointerType === "touch") {
              e.preventDefault();
            }
            onPointerDownOutside?.(e);
          }}
          className={cn(sheetContentVariants({ side, size }), className)}
          {...props}
        >
          {children}
          {!hideCloseButton && (
            <DialogPrimitive.Close asChild>
              <CloseButton size="sm" className="absolute right-5 top-5" />
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    );
  }
);

SheetContent.displayName = "SheetContent";

export { sheetContentVariants };
export type { SheetContentProps };
export default SheetContent;
